import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import VivekanandaQuote from './VivekanandaQuote';

function useCountUp(target, duration = 1800, trigger = false) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!trigger) return;
    let v = 0; const step = target / (duration / 16);
    const t = setInterval(() => { v += step; if (v >= target) { setCount(target); clearInterval(t); } else setCount(Math.floor(v)); }, 16);
    return () => clearInterval(t);
  }, [target, duration, trigger]);
  return count;
}

function useInView(threshold = 0.25) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true); }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

function Stat({ value, suffix, label, icon, delay, trigger }) {
  const n = useCountUp(value, 1800, trigger);
  return (
    <div className="h-stat" style={{ animationDelay: delay }}>
      <span className="h-stat-icon">{icon}</span>
      <span className="h-stat-num">{n}{suffix}</span>
      <span className="h-stat-lbl">{label}</span>
    </div>
  );
}

/*
  ─────────────────────────────────────────────────────────────────
  VIVEKANANDA IMAGES — HOW TO ADD THEM
  ─────────────────────────────────────────────────────────────────
  Wikimedia's upload.wikimedia.org blocks hotlinking from external
  apps (Referrer-Policy header). The fix is to store images locally.

  1. Download these 4 public-domain images:
       v1.jpg → https://commons.wikimedia.org/wiki/File:Vivekananda.jpg
       v2.jpg → https://commons.wikimedia.org/wiki/File:Swami_Vivekananda2.jpg
       v3.jpg → https://commons.wikimedia.org/wiki/File:Swami_Vivekananda_in_San_Francisco_1900_Jnana_Yoga.jpg
       v4.jpg → https://commons.wikimedia.org/wiki/File:Vivekananda-1895.jpg

  2. Place them in:   public/vivekananda/v1.jpg  (v2, v3, v4)

  Until you add them, a styled placeholder shows instead.
  ─────────────────────────────────────────────────────────────────
*/
const VIVE = [
  { src: '/vivekananda/v1.jpg', caption: 'Swami Vivekananda',                 year: '~1893'         },
  { src: '/vivekananda/v2.jpg', caption: 'Parliament of World Religions',     year: 'Chicago, 1893' },
  { src: '/vivekananda/v3.jpg', caption: 'San Francisco',                     year: '1900'          },
  { src: '/vivekananda/v4.jpg', caption: 'Portrait',                          year: '1895'          },
];

function ImgWithFallback({ src, alt, style = {} }) {
  const [err, setErr] = useState(false);
  if (err) {
    return (
      <div style={{ width: '100%', height: '100%', background: 'linear-gradient(160deg,rgba(243,156,18,.15),rgba(33,128,141,.2))', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '.4rem' }}>
        <span style={{ fontSize: '3rem', opacity: .5 }}>🙏</span>
        <span style={{ color: 'rgba(255,255,255,.6)', fontSize: '.75rem', fontFamily: "'DM Sans',sans-serif", textAlign: 'center', padding: '0 .75rem' }}>{alt}</span>
      </div>
    );
  }
  return <img src={src} alt={alt} style={style} onError={() => setErr(true)} />;
}

function ViveGallery() {
  const [active, setActive] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setActive(p => (p + 1) % VIVE.length), 4500);
    return () => clearInterval(t);
  }, []);
  return (
    <div className="h-vgal">
      <div className="h-vgal-main">
        {VIVE.map((img, i) => (
          <div key={i} className="h-vgal-slide" style={{ opacity: i === active ? 1 : 0, zIndex: i === active ? 1 : 0 }}>
            <ImgWithFallback src={img.src} alt={img.caption} style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top center', filter: 'sepia(.1) contrast(1.05)' }} />
            <div className="h-vgal-caption"><strong>{img.caption}</strong><span>{img.year}</span></div>
          </div>
        ))}
        <div className="h-vgal-ring h-vgal-ring--1" /><div className="h-vgal-ring h-vgal-ring--2" />
      </div>
      <div className="h-vgal-thumbs">
        {VIVE.map((img, i) => (
          <button key={i} className={`h-vgal-thumb${i === active ? ' active' : ''}`} onClick={() => setActive(i)}>
            <ImgWithFallback src={img.src} alt={img.caption} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          </button>
        ))}
      </div>
    </div>
  );
}

export default function Home() {
  const navigate = useNavigate();
  const [statsRef, statsInView] = useInView(0.3);

  const stats = [
    { value: 5,   suffix: '+', label: 'Years of Service',   icon: '🗓️', delay: '0ms'   },
    { value: 20,  suffix: '+', label: 'Projects Completed', icon: '✅', delay: '100ms' },
    { value: 500, suffix: '+', label: 'Lives Touched',      icon: '❤️', delay: '200ms' },
    { value: 10,  suffix: '+', label: 'Schools Reached',    icon: '🏫', delay: '300ms' },
  ];

  const pillars = [
    { icon: '📚', title: 'Education',    desc: 'Empowering government school students with quality learning, stationery drives & mentorship.',   color: '#21808d' },
    { icon: '🏥', title: 'Healthcare',   desc: 'Medical camps, health awareness drives, and medicine kits for underserved communities.',          color: '#e67e22' },
    { icon: '🙏', title: 'Spirituality', desc: "Nurturing inner growth through Vivekananda's teachings, youth workshops & values programmes.",    color: '#8e44ad' },
    { icon: '🌿', title: 'Environment',  desc: 'Tree-planting drives, clean-up campaigns & awareness of sustainable practices in communities.',   color: '#27ae60' },
  ];

  return (
    <>
      <style>{CSS}</style>

      {/* HERO */}
      <section className="h-hero">
        <div className="h-orb h-orb--a"/><div className="h-orb h-orb--b"/><div className="h-orb h-orb--c"/>
        <div className="h-hero-inner">
          <div className="h-hero-text">
            <div className="h-badge"><span className="h-bdot"/>Est. 2020 · Bengaluru, Karnataka</div>
            <h1 className="h-h1">Serving Humanity,<br/><span className="h-gold">One Life at a Time</span></h1>
            <p className="h-sub">Inspired by Swami Vivekananda's vision — a youth-driven, MSME-registered trust dedicated to education, healthcare, and community upliftment across Karnataka.</p>
            <div className="h-btns">
              <button className="h-btn-p" onClick={() => navigate('/projects')}>View Our Work <span className="h-arr">→</span></button>
              <button className="h-btn-s" onClick={() => navigate('/volunteer')}>Join the Mission</button>
            </div>
            <div className="h-motto"><span className="h-ml"/><span>"Service to Mankind is Service to God"</span><span className="h-ml"/></div>
          </div>
          <div className="h-hero-right">
            <ViveGallery/>
            <div className="h-chip h-chip--tl">🎓 Education First</div>
            <div className="h-chip h-chip--br">🌍 Nation Building</div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="h-stats" ref={statsRef}>
        <div className="h-wrap">
          <div className="h-slabel">Our Impact</div>
          <h2 className="h-h2">Five Years of Grassroots Change</h2>
          <div className="h-stats-g">{stats.map((s,i)=><Stat key={i} {...s} trigger={statsInView}/>)}</div>
        </div>
      </section>

      {/* QUOTE */}
      <section className="h-qband"><div className="h-qbg"/><div className="h-wrap" style={{position:'relative',zIndex:1}}><VivekanandaQuote/></div></section>

      {/* PILLARS */}
      <section className="h-pillars">
        <div className="h-wrap">
          <div className="h-slabel">What We Do</div>
          <h2 className="h-h2">Four Pillars of Service</h2>
          <p className="h-body" style={{maxWidth:'58ch',marginBottom:'2.5rem'}}>We work at the grassroots level across four interconnected domains, creating holistic and lasting change.</p>
          <div className="h-pillars-g">
            {pillars.map((p,i)=>(
              <div key={i} className="h-pillar" style={{'--pc':p.color}}>
                <span className="h-p-icon">{p.icon}</span>
                <h3 className="h-p-title">{p.title}</h3>
                <p className="h-p-desc">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SWAMI */}
      <section className="h-swami">
        <div className="h-sbg"/>
        <div className="h-swami-inner">
          <div className="h-sstack">
            {VIVE.slice(0,3).map((img,i)=>(
              <div key={i} className="h-scard" style={{zIndex:3-i,transform:`rotate(${[-5,0,5][i]}deg) translateY(${[0,20,40][i]}px)`}}>
                <ImgWithFallback src={img.src} alt={img.caption} style={{width:'100%',height:'100%',objectFit:'cover',objectPosition:'top'}}/>
              </div>
            ))}
          </div>
          <div className="h-stext">
            <div className="h-slabel" style={{color:'rgba(255,255,255,.5)'}}>Our Inspiration</div>
            <h2 className="h-h2" style={{color:'#fff',marginBottom:'1rem'}}>Guided by Swami Vivekananda</h2>
            <p className="h-body" style={{color:'rgba(255,255,255,.68)',marginBottom:'1.5rem'}}>Every programme, every camp, every stationery kit we distribute is an act of living out his vision: that service to the poorest is service to God. His call — "Arise, Awake, Stop Not" — is the heartbeat of Viveka Seva Trust.</p>
            <div className="h-timeline">
              {[['1863','Born in Kolkata'],['1893','Parliament of World Religions, Chicago'],['1897','Founded Ramakrishna Mission'],['1902','Attained Mahasamadhi']].map(([yr,lbl],i)=>(
                <div key={i} className="h-tl"><strong>{yr}</strong><span>{lbl}</span></div>
              ))}
            </div>
            <button className="h-btn-o" onClick={()=>navigate('/about')}>Learn About Our Mission →</button>
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <section className="h-about">
        <div className="h-abg"/>
        <div className="h-about-inner">
          <div>
            <div className="h-slabel" style={{color:'rgba(255,255,255,.5)'}}>Who We Are</div>
            <h2 className="h-h2" style={{color:'#fff'}}>A Youth-Driven,<br/>MSME-Registered Trust</h2>
            <p className="h-body" style={{color:'rgba(255,255,255,.65)',marginBottom:'1.25rem'}}>Founded in 2020 and officially registered in 2025, Viveka Seva Trust is a non-political community movement born in Bengaluru, serving government schools across Karnataka.</p>
            <div className="h-tags">{['MSME Registered','Non-Political','Youth-Led','Karnataka-Based'].map(t=><span key={t} className="h-tag">{t}</span>)}</div>
            <button className="h-btn-o" onClick={()=>navigate('/about')}>Learn More →</button>
          </div>
          <div className="h-icards">
            {[{icon:'🏛️',h:'UDYAM Registration',b:'UDYAM-KR-03-0569132'},{icon:'📍',h:'Headquartered in',b:'Hanumanth Nagar, Bengaluru'},{icon:'🎓',h:'Service Certificate',b:'Issued to all volunteers'},{icon:'🤝',h:'Open to All',b:'Anyone can join & serve'}].map((c,i)=>(
              <div key={i} className="h-icard"><span className="h-icard-icon">{c.icon}</span><div><strong>{c.h}</strong><span>{c.b}</span></div></div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="h-cta">
        <div className="h-wrap">
          <div className="h-cta-g">
            {[{cls:'h-cta-vol',icon:'🙌',title:'Volunteer With Us',desc:"Give your time and skills. Make a real difference in children's lives across Karnataka.",path:'/volunteer',cta:'Join as Volunteer'},
              {cls:'h-cta-don',icon:'💛',title:'Support Our Mission',desc:'A small donation helps us buy stationery, run health camps, and reach more communities.',path:'/donate',cta:'Donate Now'}
            ].map((c,i)=>(
              <div key={i} className={`h-cta-card ${c.cls}`}>
                <div className="h-cta-cbg"/>
                <div className="h-cta-cnt">
                  <span style={{fontSize:'2.5rem',display:'block',marginBottom:'1rem'}}>{c.icon}</span>
                  <h3 className="h-cta-title">{c.title}</h3>
                  <p className="h-cta-desc">{c.desc}</p>
                  <button className="h-btn-p" onClick={()=>navigate(c.path)}>{c.cta} →</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,500;0,700;0,900;1,500&family=DM+Sans:wght@300;400;500;600&display=swap');
.h-wrap{max-width:1200px;margin:0 auto;padding:0 2rem;}
.h-slabel{font-family:'DM Sans',sans-serif;font-size:.7rem;font-weight:600;letter-spacing:.16em;text-transform:uppercase;color:#21808d;margin-bottom:.4rem;}
.h-h2{font-family:'Playfair Display',Georgia,serif;font-size:clamp(1.8rem,3.5vw,2.6rem);font-weight:700;color:#0b3d4a;margin:0 0 .5rem;line-height:1.2;}
.h-body{font-family:'DM Sans',sans-serif;font-size:.97rem;color:#626c71;line-height:1.75;margin:0;}
.h-hero{position:relative;overflow:hidden;min-height:92vh;display:flex;align-items:center;padding:5rem 2rem 4rem;background:linear-gradient(135deg,#0b3d4a 0%,#134d5e 45%,#0e3a4a 100%);}
.h-orb{position:absolute;border-radius:50%;filter:blur(90px);opacity:.16;pointer-events:none;}
.h-orb--a{width:650px;height:650px;background:radial-gradient(circle,#e67e22,transparent);top:-180px;right:-120px;}
.h-orb--b{width:420px;height:420px;background:radial-gradient(circle,#2196a4,transparent);bottom:-120px;left:-80px;}
.h-orb--c{width:300px;height:300px;background:radial-gradient(circle,#f39c12,transparent);top:55%;left:42%;transform:translate(-50%,-50%);}
.h-hero-inner{position:relative;z-index:1;max-width:1200px;margin:0 auto;width:100%;display:grid;grid-template-columns:1fr 380px;gap:4rem;align-items:center;}
.h-badge{display:inline-flex;align-items:center;gap:8px;background:rgba(255,255,255,.1);border:1px solid rgba(255,255,255,.2);color:rgba(255,255,255,.85);font-family:'DM Sans',sans-serif;font-size:.78rem;letter-spacing:.06em;padding:6px 14px;border-radius:999px;margin-bottom:1.5rem;backdrop-filter:blur(8px);}
.h-bdot{width:7px;height:7px;background:#f39c12;border-radius:50%;animation:hPulse 2s ease-in-out infinite;flex-shrink:0;}
@keyframes hPulse{0%,100%{opacity:1;transform:scale(1)}50%{opacity:.5;transform:scale(1.5)}}
.h-h1{font-family:'Playfair Display',Georgia,serif;font-size:clamp(2.2rem,5vw,3.6rem);font-weight:700;color:#fff;line-height:1.13;margin:0 0 1.25rem;animation:hUp .7s ease both;}
.h-gold{background:linear-gradient(90deg,#f39c12,#e67e22);-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text;}
@keyframes hUp{from{opacity:0;transform:translateY(22px)}to{opacity:1;transform:translateY(0)}}
.h-sub{font-family:'DM Sans',sans-serif;font-size:1.05rem;color:rgba(255,255,255,.72);line-height:1.7;max-width:52ch;margin-bottom:2rem;animation:hUp .7s .15s ease both;}
.h-btns{display:flex;gap:1rem;flex-wrap:wrap;margin-bottom:2rem;animation:hUp .7s .3s ease both;}
.h-btn-p{display:inline-flex;align-items:center;gap:.5rem;background:linear-gradient(135deg,#e67e22,#f39c12);color:#fff;border:none;padding:.75rem 1.6rem;border-radius:8px;font-family:'DM Sans',sans-serif;font-size:.95rem;font-weight:600;cursor:pointer;transition:transform .2s,box-shadow .2s;box-shadow:0 4px 20px rgba(230,126,34,.35);}
.h-btn-p:hover{transform:translateY(-2px);box-shadow:0 8px 28px rgba(230,126,34,.48);}
.h-arr{transition:transform .2s;}
.h-btn-p:hover .h-arr{transform:translateX(4px);}
.h-btn-s{display:inline-flex;align-items:center;background:rgba(255,255,255,.1);color:#fff;border:1px solid rgba(255,255,255,.25);padding:.75rem 1.6rem;border-radius:8px;font-family:'DM Sans',sans-serif;font-size:.95rem;font-weight:500;cursor:pointer;transition:background .2s,transform .2s;backdrop-filter:blur(6px);}
.h-btn-s:hover{background:rgba(255,255,255,.18);transform:translateY(-2px);}
.h-btn-o{display:inline-flex;align-items:center;background:transparent;color:#fff;border:1px solid rgba(255,255,255,.35);padding:.65rem 1.4rem;border-radius:8px;font-family:'DM Sans',sans-serif;font-size:.88rem;font-weight:500;cursor:pointer;transition:all .2s;margin-top:1.5rem;}
.h-btn-o:hover{background:rgba(255,255,255,.1);transform:translateY(-1px);}
.h-motto{display:flex;align-items:center;gap:.75rem;font-family:'DM Sans',sans-serif;font-size:.78rem;color:rgba(255,255,255,.42);letter-spacing:.06em;font-style:italic;animation:hUp .7s .45s ease both;}
.h-ml{flex:1;height:1px;background:rgba(255,255,255,.14);max-width:60px;}
.h-hero-right{position:relative;animation:hUp .9s .2s ease both;}
.h-vgal{display:flex;flex-direction:column;gap:1rem;align-items:center;}
.h-vgal-main{position:relative;width:280px;height:350px;border-radius:16px;overflow:hidden;box-shadow:0 28px 64px rgba(0,0,0,.55);border:2px solid rgba(255,255,255,.1);}
.h-vgal-slide{position:absolute;inset:0;transition:opacity .8s ease;}
.h-vgal-caption{position:absolute;bottom:0;left:0;right:0;padding:.85rem 1rem;background:linear-gradient(to top,rgba(0,0,0,.85),transparent);display:flex;flex-direction:column;gap:2px;font-family:'DM Sans',sans-serif;color:#fff;z-index:1;}
.h-vgal-caption strong{font-size:.88rem;font-weight:600;}
.h-vgal-caption span{font-size:.72rem;opacity:.6;}
.h-vgal-ring{position:absolute;border-radius:50%;border:1px solid rgba(243,156,18,.22);pointer-events:none;z-index:-1;}
.h-vgal-ring--1{width:360px;height:360px;top:50%;left:50%;transform:translate(-50%,-50%);}
.h-vgal-ring--2{width:310px;height:310px;top:50%;left:50%;transform:translate(-50%,-50%);border-color:rgba(243,156,18,.11);}
.h-vgal-thumbs{display:flex;gap:.5rem;}
.h-vgal-thumb{width:54px;height:54px;border-radius:8px;overflow:hidden;border:2px solid rgba(255,255,255,.15);cursor:pointer;transition:border-color .2s,transform .2s;padding:0;background:rgba(255,255,255,.08);display:flex;align-items:center;justify-content:center;}
.h-vgal-thumb.active{border-color:#f39c12;transform:scale(1.08);}
.h-chip{position:absolute;background:rgba(255,255,255,.12);backdrop-filter:blur(10px);border:1px solid rgba(255,255,255,.2);color:#fff;font-family:'DM Sans',sans-serif;font-size:.72rem;font-weight:500;padding:5px 11px;border-radius:999px;white-space:nowrap;box-shadow:0 4px 14px rgba(0,0,0,.2);animation:hFloat 3s ease-in-out infinite;}
.h-chip--tl{top:-12px;left:-36px;animation-delay:0s;}
.h-chip--br{bottom:60px;right:-44px;animation-delay:1.5s;}
@keyframes hFloat{0%,100%{transform:translateY(0)}50%{transform:translateY(-6px)}}
.h-stats{background:#f5f4f0;padding:5rem 0;border-top:1px solid rgba(94,82,64,.1);}
.h-stats-g{display:grid;grid-template-columns:repeat(4,1fr);gap:1.25rem;margin-top:2.5rem;}
.h-stat{background:#fff;border:1px solid rgba(94,82,64,.1);border-radius:14px;padding:2rem 1.25rem;text-align:center;box-shadow:0 2px 12px rgba(0,0,0,.04);display:flex;flex-direction:column;align-items:center;gap:.4rem;transition:transform .25s,box-shadow .25s;}
.h-stat:hover{transform:translateY(-4px);box-shadow:0 10px 28px rgba(0,0,0,.08);}
.h-stat-icon{font-size:1.8rem;}
.h-stat-num{font-family:'Playfair Display',Georgia,serif;font-size:2.5rem;font-weight:700;color:#21808d;line-height:1;}
.h-stat-lbl{font-family:'DM Sans',sans-serif;font-size:.8rem;color:#888;font-weight:500;}
.h-qband{position:relative;padding:4rem 0;overflow:hidden;}
.h-qbg{position:absolute;inset:0;background:linear-gradient(135deg,#0b3d4a 0%,#0e4f5e 65%,#1a6b7a 100%);}
.h-pillars{padding:5rem 0;background:#f5f4f0;}
.h-pillars-g{display:grid;grid-template-columns:repeat(4,1fr);gap:1.25rem;}
.h-pillar{background:#fff;border:1px solid rgba(94,82,64,.1);border-top:4px solid var(--pc,#21808d);border-radius:14px;padding:1.75rem 1.5rem;box-shadow:0 2px 10px rgba(0,0,0,.04);transition:transform .25s,box-shadow .25s;}
.h-pillar:hover{transform:translateY(-6px);box-shadow:0 14px 36px rgba(0,0,0,.1);}
.h-p-icon{font-size:2rem;display:block;margin-bottom:.85rem;}
.h-p-title{font-family:'Playfair Display',Georgia,serif;font-size:1.15rem;font-weight:600;color:#0b3d4a;margin:0 0 .5rem;}
.h-p-desc{font-family:'DM Sans',sans-serif;font-size:.85rem;color:#626c71;line-height:1.65;margin:0;}
.h-swami{position:relative;overflow:hidden;padding:5rem 2rem;}
.h-sbg{position:absolute;inset:0;background:linear-gradient(135deg,#0b3d4a 0%,#134d5e 60%,#0e3a4a 100%);}
.h-swami-inner{position:relative;z-index:1;max-width:1200px;margin:0 auto;display:grid;grid-template-columns:260px 1fr;gap:5rem;align-items:center;}
.h-sstack{position:relative;height:280px;width:260px;flex-shrink:0;}
.h-scard{position:absolute;width:160px;height:200px;border-radius:12px;overflow:hidden;border:3px solid rgba(255,255,255,.15);box-shadow:0 16px 48px rgba(0,0,0,.4);}
.h-scard:nth-child(1){top:0;left:0;}
.h-scard:nth-child(2){top:20px;left:40px;}
.h-scard:nth-child(3){top:40px;left:80px;}
.h-timeline{display:grid;grid-template-columns:1fr 1fr;gap:.75rem 1.5rem;margin-bottom:.5rem;}
.h-tl{display:flex;flex-direction:column;gap:2px;}
.h-tl strong{font-family:'Playfair Display',serif;font-size:1.1rem;color:#f39c12;}
.h-tl span{font-family:'DM Sans',sans-serif;font-size:.78rem;color:rgba(255,255,255,.5);}
.h-about{position:relative;overflow:hidden;padding:5rem 2rem;}
.h-abg{position:absolute;inset:0;background:linear-gradient(135deg,#7d3c00 0%,#a04000 50%,#7d3000 100%);}
.h-about-inner{position:relative;z-index:1;max-width:1200px;margin:0 auto;display:grid;grid-template-columns:1fr 1fr;gap:4rem;align-items:center;}
.h-tags{display:flex;flex-wrap:wrap;gap:.5rem;margin-bottom:.5rem;}
.h-tag{background:rgba(255,255,255,.1);border:1px solid rgba(255,255,255,.2);color:rgba(255,255,255,.85);font-family:'DM Sans',sans-serif;font-size:.72rem;font-weight:500;padding:4px 12px;border-radius:999px;}
.h-icards{display:grid;grid-template-columns:1fr 1fr;gap:.85rem;}
.h-icard{background:rgba(255,255,255,.08);border:1px solid rgba(255,255,255,.12);border-radius:12px;padding:1rem 1.1rem;display:flex;align-items:center;gap:.85rem;backdrop-filter:blur(6px);transition:background .2s;}
.h-icard:hover{background:rgba(255,255,255,.14);}
.h-icard-icon{font-size:1.4rem;flex-shrink:0;}
.h-icard strong{display:block;color:#fff;font-family:'DM Sans',sans-serif;font-size:.85rem;font-weight:600;}
.h-icard span{color:rgba(255,255,255,.5);font-family:'DM Sans',sans-serif;font-size:.75rem;}
.h-cta{padding:5rem 0;background:#f5f4f0;}
.h-cta-g{display:grid;grid-template-columns:1fr 1fr;gap:1.25rem;}
.h-cta-card{position:relative;border-radius:16px;padding:3rem 2.5rem;overflow:hidden;min-height:280px;display:flex;align-items:flex-end;transition:transform .25s;}
.h-cta-card:hover{transform:translateY(-4px);}
.h-cta-cbg{position:absolute;inset:0;}
.h-cta-vol .h-cta-cbg{background:linear-gradient(135deg,#0b3d4a,#1a6b7a);}
.h-cta-don .h-cta-cbg{background:linear-gradient(135deg,#7d3c00,#c0601a);}
.h-cta-cnt{position:relative;z-index:1;}
.h-cta-title{font-family:'Playfair Display',Georgia,serif;font-size:1.6rem;color:#fff;margin:0 0 .6rem;font-weight:700;}
.h-cta-desc{font-family:'DM Sans',sans-serif;font-size:.88rem;color:rgba(255,255,255,.68);line-height:1.6;margin-bottom:1.5rem;max-width:36ch;}
@media(max-width:1000px){.h-hero-inner{grid-template-columns:1fr;text-align:center;}.h-hero-right{display:none;}.h-btns{justify-content:center;}.h-motto{justify-content:center;}.h-stats-g{grid-template-columns:repeat(2,1fr);}.h-pillars-g{grid-template-columns:repeat(2,1fr);}.h-swami-inner{grid-template-columns:1fr;gap:2.5rem;}.h-sstack{display:none;}.h-about-inner{grid-template-columns:1fr;gap:2.5rem;}.h-cta-g{grid-template-columns:1fr;}}
@media(max-width:600px){.h-stats-g{grid-template-columns:repeat(2,1fr);gap:.85rem;}.h-pillars-g{grid-template-columns:1fr;}.h-icards{grid-template-columns:1fr;}.h-hero{padding:4rem 1.25rem 3rem;}}
`;